import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Checkbox, Clear } from 'basic-components';
import { Query } from 'dashboard-services';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { seriesHooks } from 'hooks';
import { dataStorageQueries } from 'queries/datastorage';
import properties from 'resources/constants/properties.json';
import { LocalStorageUtils } from 'utils';

import { FocusTrap } from 'focus-trap-react';

import classNames from 'classnames';
import './FiltersSetUp.scss';

const emptyFun = () => {}
const emptyArray = []
const FiltersSetUp = ({ onClose }) => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.source),
        [filters, setFilters] = useState([]),
        [defaultEnvKeys, setDefaultEnvKeys] = useState([]),
        resetToDefault = useCallback(() => setFilters(defaultEnvKeys), [defaultEnvKeys]),
        saveUserFilters = useCallback(() => {
          dispatch(creatorSeriesActions.onChange({}, "filters"))
          dispatch(creatorSeriesActions.onChange(filters, "userDefinedFilters"))
          if(JSON.stringify(defaultEnvKeys) === JSON.stringify(filters)) {
            LocalStorageUtils.setSavedSeriesFilters("")
          } else {
            LocalStorageUtils.setSavedSeriesFilters(filters)
          }
          onClose()
        }, [defaultEnvKeys, dispatch, filters, onClose]),
        select = useCallback(data => data?.pages?.flatMap(page => page?.items)?.map(ek => ({ label: ek.name, type: ek.type, filterKey: ek.name })).filter(ek => defaultEnvKeys.findIndex(dek => dek.filterKey === ek.filterKey && dek.type === ek.type) === -1), [defaultEnvKeys]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", source, true)
        , [getApiConfigLocal, source]),
        { data: allEnvKeys = emptyArray } = dataStorageQueries.useSearchSymbolKeys({
          getQueryBuilder,
          query: undefined,
          groupName: "",
          useEs: properties.useEsSymbolListing === "true",
          size: 1000,
          metadata: true,
          getApiConfig: getApiConfigLocal,
          select,
          provider: source
        }),
        availableFilters = useMemo(() => {
          let list = [].concat(defaultEnvKeys)
          list = list.concat([{
            label: "Group",
            type: creatorSeriesActions.FILTER_TYPE.GROUP_NAME,
            filterKey: creatorSeriesActions.FILTER_TYPE.GROUP_NAME
          }])
          list = list.concat(allEnvKeys)
          return list;
        }, [allEnvKeys, defaultEnvKeys]),
        getUserFilters = seriesHooks.useUserFilters(),
        getEnvFilters = seriesHooks.useEnvFilters(),
        onKeyDown = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            onClose()
          }
        }, [onClose])

  useEffect(() => {
    getUserFilters().then(setFilters)
  }, [dispatch, getUserFilters])

  useEffect(() => {
    getEnvFilters().then(setDefaultEnvKeys)
  }, [dispatch, getEnvFilters])

  return createPortal((
    <FocusTrap>
      <div className="series__creator__filters-set-up">
        <div className="series__creator__filters-set-up__headers">
          <div className="series__creator__filters-set-up__left">
            Active Filters
          </div>
          <div className="series__creator__filters-set-up__right">
            <Button
                onClick={resetToDefault}
                size="small"
                variant="text"
            >
              Default
            </Button>
            <div 
                className="series__creator__filters-set-up__right__close"
                onClick={onClose}
                onKeyDown={onKeyDown}
                tabIndex={0}
            >
              <Clear 
                  color="grey-dark"
                  height={8}
                  width={8}
              />
            </div>
          </div>
        </div>
        <div className="series__creator__filters-set-up__body">
          {availableFilters.map(af => {
            const itemIndex = filters.findIndex(f => f.filterKey === af.filterKey && f.type === af.type)
            return (
              <div 
                  className={classNames(
                    "series__creator__filters-set-up__body__row",
                    { "series__creator__filters-set-up__body__row--is-selected": itemIndex > -1 }
                  )}
                  key={af.filterKey + af.type + itemIndex}
                  onClick={() => setFilters(filters => {
                    if(itemIndex > -1) {
                      return filters.remove(itemIndex)
                    }
                    return filters.add(af)
                  })}
              >
                <Checkbox
                    checked={itemIndex > -1}
                    disabled={itemIndex > -1 && filters.length === 1}
                    onChange={emptyFun}
                >
                  {af.label}
                </Checkbox>
              </div>
            )
          })}
        </div>
        <div className="series__creator__filters-set-up__footer">
          <Button 
              onClick={saveUserFilters}
          >
            Save
          </Button>
        </div>
      </div>
    </FocusTrap>
  ), document.body)
}

export default FiltersSetUp;